import React, { useState, useCallback, useEffect } from 'react';
import { createPortal } from 'react-dom';
import AchievementToast from '../AchievementToast/AchievementToast';
import Toast from '../Toast/Toast';
import './ToastManager.scss';

const ToastManager = ({ toasts, onRemoveToast }) => {
  const [toastContainer, setToastContainer] = useState(null);

  useEffect(() => {
    // Create or get the toast container
    let container = document.getElementById('toast-container');
    if (!container) {
      container = document.createElement('div');
      container.id = 'toast-container';
      container.className = 'toast-manager__container';
      document.body.appendChild(container);
    }
    setToastContainer(container);

    // Cleanup function
    return () => {
      if (container && container.parentNode && toasts.length === 0) {
        container.parentNode.removeChild(container);
      }
    };
  }, [toasts.length]);

  const handleRemoveToast = useCallback((toastId) => {
    onRemoveToast(toastId);
  }, [onRemoveToast]);

  const handleKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && toasts.length > 0) {
      // Close the most recent toast
      const latestToast = toasts[toasts.length - 1];
      if (latestToast) {
        handleRemoveToast(latestToast.id);
      }
    }
  }, [toasts.length, handleRemoveToast]); // Only depend on length to prevent unnecessary re-renders

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [handleKeyDown]);

  if (!toastContainer || toasts.length === 0) {
    return null;
  }

  return createPortal(
    <div className="toast-manager">
      {toasts.map((toast, index) => (
        <div
          key={toast.id}
          className="toast-manager__toast"
          style={{
            // Stack toasts vertically
            marginBottom: index === toasts.length - 1 ? '0' : '15px',
            zIndex: 9999 - index,
            opacity: index < 3 ? 1 : 0, // Show max 3 toasts at once
            pointerEvents: index < 3 ? 'auto' : 'none'
          }}
        >
          {toast.type === 'achievement' ? (
            <AchievementToast
              achievement={toast.achievement}
              onClose={() => handleRemoveToast(toast.id)}
              autoClose={toast.autoClose}
              autoCloseDelay={toast.autoCloseDelay}
            />
          ) : (
            <Toast
              message={toast.message}
              type={toast.type}
              onClose={() => handleRemoveToast(toast.id)}
              autoClose={toast.autoClose}
              autoCloseDelay={toast.autoCloseDelay}
            />
          )}
        </div>
      ))}
    </div>,
    toastContainer
  );
};

export default ToastManager;